import React, { memo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AppSpinner from "./AppSpinner";
import { Tooltip } from "react-tooltip";
import "../styles/MauiWidget.css";
import "react-tooltip/dist/react-tooltip.css";

import { mauiServices } from "../http-common";

// M8: Migration von react-table v7 (unmaintained) auf @tanstack/react-table v8.
// API-Wechsel: useTable + Plugin-Hooks (useSortBy, useFilters) -> useReactTable
// + getCoreRowModel/getFilteredRowModel/getSortedRowModel via getXxxRowModel().
// flexRender ersetzt column.render('Header'/'Cell'); column.getXxx()-Methoden
// statt direkter Properties (canFilter -> getCanFilter, isSorted -> getIsSorted etc.).
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

/**
 * Tabelle für die Downtimeauswahl
 * @returns
 */
function Table({ data }) {
  /**
   * Input-Element für die Filterung der Tabelle
   *
   * @param {*} column
   * @returns
   */
  const ColumnFilter = ({ column }) => {
    // v8: column.getFilterValue()/setFilterValue() ersetzen v7 column.filterValue/setFilter.
    const filterValue = column.getFilterValue() ?? "";
    return (
      <div className="maui-Table-SearchWrapper">
        <input
          className="maui-Table-SearchInput"
          value={filterValue}
          onChange={(e) => column.setFilterValue(e.target.value)}
          onClick={(e) => e.stopPropagation()} // Hier wird das Klickereignis gestoppt
          placeholder="Suche..."
        />
        <div
          className="maui-Table-SearchReset"
          onClick={(e) => {
            e.stopPropagation(); // Hier wird das Klickereignis gestoppt
            column.setFilterValue("");
          }}
        >
          <FontAwesomeIcon icon="fa-solid fa-arrow-rotate-left" />
        </div>
      </div>
    );
  };

  // v8 Mapping (vs. v7):
  //   accessor -> accessorKey, Header -> header, Filter -> meta.Filter (custom slot, von uns
  //   selbst gerendert), sortType -> sortingFn (Signatur identisch).
  // Die per-Spalten-localeCompare-Sortierung bleibt fachlich gleich.
  const sortLocaleCi = React.useMemo(
    () => (rowA, rowB, columnId) => {
      const valueA = String(rowA.getValue(columnId) ?? "").toLowerCase();
      const valueB = String(rowB.getValue(columnId) ?? "").toLowerCase();
      return valueA.localeCompare(valueB);
    },
    []
  );

  const columns = React.useMemo(
    () => [
      {
        header: "Backend",
        accessorKey: "backend",
        meta: { Filter: ColumnFilter },
        sortingFn: sortLocaleCi,
      },
      {
        header: "Purpose",
        accessorKey: "purpose",
        meta: { Filter: ColumnFilter },
        sortingFn: sortLocaleCi,
      },
      {
        header: "Contact",
        accessorKey: "contact",
        meta: { Filter: ColumnFilter },
        sortingFn: sortLocaleCi,
      },
      {
        header: "Technology",
        accessorKey: "technology",
        meta: { Filter: ColumnFilter },
        sortingFn: sortLocaleCi,
      },
      {
        header: "Server",
        accessorKey: "server",
        meta: { Filter: ColumnFilter },
        sortingFn: sortLocaleCi,
      },
      {
        header: "Port",
        accessorKey: "port",
        meta: { Filter: ColumnFilter },
        sortingFn: sortLocaleCi,
      },
      {
        header: "Status",
        accessorKey: "status",
        meta: { Filter: ColumnFilter },
        sortingFn: sortLocaleCi,
      },
      {
        header: "Test Duration",
        accessorKey: "testDuration",
        meta: { Filter: ColumnFilter },
        // Kein sortingFn -> v8 nimmt Default-Sortierung anhand des Wert-Typs.
      },
    ],
    [sortLocaleCi]
  );

  // v8: useReactTable + getXxxRowModel statt v7 useTable(...,useFilters,useSortBy).
  // Filter-/Sort-State liegt jetzt explizit als React-State vor und wird ueber
  // onColumnFiltersChange/onSortingChange aktualisiert.
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [sorting, setSorting] = React.useState([]);

  const table = useReactTable({
    columns,
    data,
    state: { columnFilters, sorting },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <table className="maui-table">
      <thead className="maui-table-head">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className="maui-tableHead"
                onClick={header.column.getToggleSortingHandler()}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
                <span>
                  {header.column.getIsSorted() === "desc" ? (
                    <FontAwesomeIcon
                      className="mauiWidget-arrow"
                      icon="fa-solid fa-arrow-down"
                    />
                  ) : header.column.getIsSorted() === "asc" ? (
                    <FontAwesomeIcon
                      className="mauiWidget-arrow"
                      icon="fa-solid fa-arrow-up"
                    />
                  ) : (
                    ""
                  )}
                </span>
                <div>
                  {header.column.getCanFilter() &&
                  header.column.columnDef.meta?.Filter
                    ? React.createElement(header.column.columnDef.meta.Filter, {
                        column: header.column,
                      })
                    : null}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} className="maui-tableCell">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const MauiWidget = (props) => {
  /***
   *     __  ___      ___  ___  __
   *    /__`  |   /\   |  |__  /__`
   *    .__/  |  /~~\  |  |___ .__/
   *
   */

  const [configMenuStatus, setConfigMenuStatus] = React.useState(false);

  const [widgetOptions, setWidgetOptions] = React.useState(() => {});

  const [optionHasChanged, setOptionHasChanged] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false); // Indikator ob Daten geladen wurden.
  const [hasError, setHasError] = React.useState(false); // Indikator ob Fehler vorliegen
  const [mauiDataState, setMauiDataState] = React.useState();


  const [initialLoad, setInitialLoad] = React.useState(true); // Neuer Zustand für das erste Laden
  
  /***
   *          __   ___     ___  ___  ___  ___  __  ___  __
   *    |  | /__` |__     |__  |__  |__  |__  /  `  |  /__`
   *    |__/ .__/ |___    |___ |    |    |___ \__,  |  .__/
   *
   */

  React.useEffect(() => {
    if (optionHasChanged === true) {
      //console.log("Option has changed in Productoverview")
      props.syncOptionsArray(props.id, widgetOptions);
      setOptionHasChanged(false);
    } else {
      //console.log("No Option has been changed in Productoverview")
    }
  }, [widgetOptions]);

  /***
   *     ___            __  ___    __        __
   *    |__  |  | |\ | /  `  |  | /  \ |\ | /__`
   *    |    \__/ | \| \__,  |  | \__/ | \| .__/
   *
   */

  const toggleConfigMenuStatus = () => {
    setConfigMenuStatus(!configMenuStatus);
  };

React.useEffect(() => {

  setIsLoaded(false)
  const fetchData = async () => {

    const idToken = props.receiveIDToken();
    const headers = { 'Authorization': idToken };

    const res = await mauiServices.get("", {headers});

    const result = {
      status: res.status + "-" + res.statusText,
      headers: res.headers,
      data: res.data,
    };

    if(res.data.status === 200){
      setMauiDataState(result.data.message);
      setIsLoaded(true);
      setInitialLoad(false); 
    } else {
      setIsLoaded(true);
      setHasError(true);
      setInitialLoad(false); 
    }

  };

  // call the function once use effect run
  fetchData().catch((error) => {
    const errorMsg = error.name + ": " + error.message;
    props.handleNotify({ type: "alert", message: errorMsg });
  });
}, [props]);


  /***********************************************************************************
   * DOM für das Konfigurationsmenü generieren
   ***********************************************************************************/
  const FillConfigMenu = () => {
    //console.log(widgetOptions.visibleTests)
    return (
      <div className="productWidget--configMenuWrap productWidgetConfig--Card">
        <div className="productWidget--HeaderWrap drag-handle productWidgetConfig--Header">
          <div className="productWidgetConfig--HeaderTextWrap">
            <span className="productWidgetConfig--HeaderMeta">MAUI SERVICES</span>
            <span className="productWidgetConfig--HeaderTitle">Widget-Konfiguration</span>
          </div>
          <button
            type="button"
            className="productWidgetHeader--MenuBtn productWidgetConfig--CloseBtn"
            onClick={() => toggleConfigMenuStatus()}
            aria-label="Konfiguration schließen"
            title="Schließen"
          >
            <FontAwesomeIcon icon="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="productWidgetConfig--ContentWrap productWidgetConfig--Body">
          <div className="productWidgetConfig--EmptyState">
            <FontAwesomeIcon icon="fa-solid fa-sliders" />
            <div>Für dieses Widget sind aktuell keine Optionen verfügbar.</div>
          </div>
          {/* Auskommentiert, solange keine Optionen in diesem Widget benötigt werden.
          <div className="productWidgetConfigContent--HeaderWrap">
            <div className="productWidgetConfigContent--HeaderTitle">
              Widget Optionen
            </div>
            <div className="productWidgetConfigContent--LineBreak"></div>
            <div className="productWidgetConfigContent--HeaderSubTitle"></div>
          </div>

          <div className="productWidgetConfigContent--ContentWrap"></div>

          <div className="productWidgetConfigContent--ProductContentWrap"></div>
          */}
        </div>
        <div className="productWidgetConfig--DeleteWrap productWidgetConfig--Footer">
          <button
            type="button"
            className="productWidgetHeader--DeleteBtn productWidgetConfig--DeleteBtn"
            onClick={() => props.handleDelete(props.id)}
          >
            <FontAwesomeIcon icon="fa-solid fa-trash" />
            <span>Widget entfernen</span>
          </button>
        </div>
      </div>
    );
  };

  const openNewWebtab = (url) => {
    console.log("Open: " + url);
    window.open(url, "_blank").focus();
  };

  /***
   *     __   __            __   ___ ___       __
   *    |  \ /  \  |\/|    |__) |__   |  |  | |__) |\ |
   *    |__/ \__/  |  |    |  \ |___  |  \__/ |  \ | \|
   *
   */

  return (
    <>
      {configMenuStatus ? <FillConfigMenu /> : null}
      <div className="productWidget--HeaderWrap drag-handle">
        <div className="mauiWidgetHeader--Title">Maui Services</div>
        <button
          className="productWidgetHeader--MenuBtn"
          onClick={() => toggleConfigMenuStatus()}
        >
          <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" />
        </button>
      </div>
      <div className="mauiWidget--ContentWrap">
        {(() => {
          if (isLoaded === false && initialLoad)
            return (
              <>
                <div className="productWidget--LoadingWrap">
                  <p className="productWidget--LoadingWrapText">Lade Daten</p>
                  <p>
                    <AppSpinner />
                  </p>
                </div>
              </>
            );
          if (hasError === true)
            return <div className="productWidget--ErrorWrap">Die Maui Daten konnten nicht geladen werden.</div>;
          else
            return (
              <div className="mauiWidget-wrapper">
                <Table data={mauiDataState} />
              </div>
            );
        })()}
      </div>
    </>
  );
};

export default memo(MauiWidget);
